import { useNavigate } from "react-router-dom";
import { ShoppingCart, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/data/catalog";
import { toast } from "sonner";

export const ProductCard = ({ product }) => {
  const { addItem, buyNow, setCartOpen } = useCart();
  const navigate = useNavigate();
  const hasDiscount = product.mrp > product.price;
  const go = () => navigate(`/products/${product.slug}`);

  const handleAdd = () => {
    addItem(product.slug);
    toast.success(`${product.name} added to cart`);
    setCartOpen(true);
  };

  return (
    <div
      data-testid={`product-card-${product.slug}`}
      className="group bg-white rounded-xl border border-[#E5E0D8] shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col"
    >
      <button
        data-testid={`view-product-${product.slug}`}
        onClick={go}
        className="relative aspect-square bg-[#F9F6F0] p-6 overflow-hidden"
      >
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <Badge className="absolute top-3 left-3 bg-[#1A4331] text-white border-none">
          {product.category}
        </Badge>
        {hasDiscount && (
          <Badge className="absolute top-3 right-3 bg-[#C05C46] text-white border-none">
            {Math.round(((product.mrp - product.price) / product.mrp) * 100)}% OFF
          </Badge>
        )}
      </button>

      <div className="p-5 flex flex-col flex-1">
        <button
          data-testid={`product-name-${product.slug}`}
          onClick={go}
          className="text-left"
        >
          <h3 className="font-display text-xl font-semibold text-[#1c2826] leading-snug">
            {product.name}
          </h3>
        </button>
        <p className="text-sm text-[#4B5563] mt-1 line-clamp-2">{product.tagline}</p>
        <p className="text-xs text-[#7C9A82] font-semibold uppercase tracking-wider mt-2">
          {product.pack}
        </p>

        <div className="flex items-baseline gap-2 mt-3">
          <span data-testid={`price-${product.slug}`} className="text-2xl font-bold text-[#1A4331]">
            {formatINR(product.price)}
          </span>
          {hasDiscount && (
            <span className="text-sm text-[#4B5563] line-through">{formatINR(product.mrp)}</span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-[#E5E0D8]">
          <Button
            data-testid={`add-to-cart-${product.slug}`}
            variant="outline"
            onClick={handleAdd}
            className="rounded-full border-[#1A4331]/40 text-[#1A4331] hover:bg-[#1A4331] hover:text-white transition-colors"
          >
            <ShoppingCart className="w-4 h-4 mr-1.5" />
            Add to Cart
          </Button>
          <Button
            data-testid={`buy-now-${product.slug}`}
            onClick={() => buyNow(product.slug)}
            className="rounded-full bg-[#C05C46] hover:bg-[#a84d3a] text-white transition-colors"
          >
            <Zap className="w-4 h-4 mr-1.5" />
            Buy Now
          </Button>
        </div>
      </div>
    </div>
  );
};
